import RechargeCooldownLabel from "../../../RechargeCooldownLabel";
import React, { useState, useEffect, useRef } from "react";
import { useTranslationPlus } from "@/Core/i18n/useTranslationPlus";
import PopupMask from "@/componentsUI/PopupMask";
import ButtonUI from "@/componentsUI/Button";
import OTPInput from "@/componentsUI/OTPInput";
import { getPaymentIcon } from "@/pages/wallet/shared/PaymentMethodUtils";
import { getCountryCodeByDomain } from "@/utils/countryConfigUtils";
import { feedback } from "@/utils/feedback";
import styles from "./index.module.less";

export interface PaymentConfirmModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (verifyCode?: string) => void;
  paymentMethod: string;
  loading?: boolean;
  disabled?: boolean;
  country?: string; // Code pays (ex: 'TG', 'SN')
}
// Convertir le code num茅rique en code pays
const getCountryCodeFromNumeric = (numericCode: number): string => {
  const mapping = {
    221: "SN", // S茅n茅gal
    225: "CI", // Côte d'Ivoire
    228: "TG", // Togo
    229: "BJ", // B茅nin
    226: "BF", // Burkina Faso
  };
  return mapping[numericCode] || "default";
};

// Configuration des modes de paiement par pays et m茅thode
const getPaymentConfig = (
  paymentMethod: string,
  countryCode: string | undefined,
  t: (key: string, options?: any) => string
) => {
  // Si pas de code pays fourni, d茅tecter automatiquement
  const country =
    countryCode || getCountryCodeFromNumeric(getCountryCodeByDomain());

  const waitTitle = t("Wallet.PaymentConfirm.WaitTitle");
  const processingContent = t("Wallet.PaymentConfirm.ProcessingContent");
  const retryNote = t("Wallet.PaymentConfirm.RetryNote");
  const verifyNote = t("Wallet.PaymentConfirm.VerifyCodeNote");
  const step1Title = t("Wallet.PaymentConfirm.Step1Title");
  const step2Title = t("Wallet.PaymentConfirm.Step2Title");
  const step3Title = t("Wallet.PaymentConfirm.Step3Title");

  const configs = {
    // Configurations pour le Togo
    TG: {
      Tmoney: {
        steps: [
          {
            content: t("Wallet.PaymentConfirm.TG.TmoneyStep1"),
          },
          {
            content: t("Wallet.PaymentConfirm.TG.TmoneyStep2"),
          },
          {
            content: t("Wallet.PaymentConfirm.TG.TmoneyStep3"),
          },
        ],
        note: retryNote,
        disableAutoTrigger: true,
      },
      Moov: {
        steps: [
          {
            content: t("Wallet.PaymentConfirm.TG.MoovStep1"),
          },
          {
            content: t("Wallet.PaymentConfirm.TG.MoovStep2"),
            hasHighlightCode: true,
            highlightCodes: ["*155*2*4#"],
          },
          {
            content: t("Wallet.PaymentConfirm.TG.MoovStep3"),
          },
        ],
        note: retryNote,
        disableAutoTrigger: true,
      },
    },
    // Configurations pour la Côte d'Ivoire
    CI: {
      Orange: {
        steps: [
          {
            content: t("Wallet.PaymentConfirm.CI.OrangeStep1"),
            hasHighlightCode: true,
            highlightCodes: ["#144*82#"],
          },
          {
            content: t("Wallet.PaymentConfirm.OrangeStep2"),
          },
          {
            content: t("Wallet.PaymentConfirm.OrangeStep3"),
          },
        ],
        note: verifyNote,
        requiresVerifyCode: true,
        verifyCodeLength: 4,
      },
      Moov: {
        steps: [
          {
            title: step1Title,
            content: t("Wallet.PaymentConfirm.MoovStep1Content"),
          },
          {
            title: step2Title,
            content: t("Wallet.PaymentConfirm.CI.MoovStep2"),
            hasHighlightCode: true,
            highlightCodes: ["*15#"],
          },
          {
            title: step3Title,
            content: t("Wallet.PaymentConfirm.MoovStep3Content"),
          },
        ],
        note: retryNote,
        disableAutoTrigger: true,
      },
      Momo: {
        steps: [
          {
            title: step1Title,
            content: t("Wallet.PaymentConfirm.MomoStep1Content"),
            hasHighlightCode: true,
            highlightCodes: ["133#"],
          },
          {
            title: step2Title,
            content: t("Wallet.PaymentConfirm.MomoStep2Content"),
          },
          {
            title: step3Title,
            content: t("Wallet.PaymentConfirm.MomoStep3Content"),
          },
        ],
        note: retryNote,
        disableAutoTrigger: true,
      },
    },
    // Configurations pour le S茅n茅gal
    SN: {
      Orange: {
        steps: [
          {
            content: t("Wallet.PaymentConfirm.OrangeAuthIntro"),
          },
          {
            content: t("Wallet.PaymentConfirm.SN.OrangeStep1"),
            hasHighlightCode: true,
            highlightCodes: ["#144#391#"],
          },
          {
            content: t("Wallet.PaymentConfirm.OrangeStep2"),
          },
          {
            content: t("Wallet.PaymentConfirm.OrangeStep3"),
          },
        ],
        note: verifyNote,
        requiresVerifyCode: true,
        verifyCodeLength: 6,
      },
    },
    // Configurations pour le B茅nin
    BJ: {
      Momo: {
        steps: [
          {
            title: waitTitle,
            content: processingContent,
          },
        ],
        note: retryNote,
      },
      Moov: {
        steps: [
          {
            title: waitTitle,
            content: processingContent,
          },
        ],
        note: retryNote,
      },
    },
    // Configurations pour le Burkina Faso
    BF: {
      Orange: {
        steps: [
          {
            content: t("Wallet.PaymentConfirm.OrangeAuthIntro"),
          },
          {
            content: t("Wallet.PaymentConfirm.BF.OrangeStep1"),
            hasHighlightCode: true,
            highlightCodes: ["*144*4*6#"],
          },
          {
            content: t("Wallet.PaymentConfirm.OrangeStep2"),
          },
          {
            content: t("Wallet.PaymentConfirm.OrangeStep3"),
          },
        ],
        note: verifyNote,
        requiresVerifyCode: true,
        verifyCodeLength: 6,
      },
      Moov: {
        steps: [
          {
            content: t("Wallet.PaymentConfirm.MoovIntro"),
          },
          {
            title: step1Title,
            content: t("Wallet.PaymentConfirm.MoovBFStep1Content"),
          },
          {
            title: step2Title,
            content: t("Wallet.PaymentConfirm.MoovBFStep2Content"),
          },
        ],
        note: retryNote,
      },
    },
    // Configurations par d茅faut (anciennes configurations)
    default: {
      Wave: {
        steps: [
          {
            title: step1Title,
            content: t("Wallet.PaymentConfirm.WaveStep1Content"),
          },
          {
            title: step2Title,
            content: t("Wallet.PaymentConfirm.WaveStep2Content"),
          },
        ],
        note: retryNote,
      },
      Momo: {
        steps: [
          {
            title: step1Title,
            content: t("Wallet.PaymentConfirm.MomoStep1Content"),
          },
          {
            title: step2Title,
            content: t("Wallet.PaymentConfirm.MomoStep2Content"),
          },
          {
            title: step3Title,
            content: t("Wallet.PaymentConfirm.MomoStep3Content"),
          },
        ],
        note: retryNote,
      },
      Orange: {
        steps: [
          {
            title: t("Wallet.PaymentConfirm.DefaultOrangeStep1"),
          },
          {
            title: t("Wallet.PaymentConfirm.OrangeStep2"),
          },
          {
            title: t("Wallet.PaymentConfirm.OrangeStep3"),
          },
        ],
        note: verifyNote,
        requiresVerifyCode: true,
        verifyCodeLength: 4,
      },
      Moov: {
        steps: [
          {
            title: step1Title,
            content: t("Wallet.PaymentConfirm.MoovStep1Content"),
          },
          {
            title: step2Title,
            content: t("Wallet.PaymentConfirm.MoovStep2Content"),
          },
          {
            title: step3Title,
            content: t("Wallet.PaymentConfirm.MoovStep3Content"),
          },
        ],
        note: retryNote,
      },
      Tmoney: {
        steps: [
          {
            title: step1Title,
            content: t("Wallet.PaymentConfirm.TmoneyStep1Content"),
          },
          {
            title: step2Title,
            content: t("Wallet.PaymentConfirm.TmoneyStep2Content"),
          },
          {
            title: step3Title,
            content: t("Wallet.PaymentConfirm.TmoneyStep3Content"),
          },
        ],
        note: retryNote,
      },
    },
  };

  // Retourner la configuration sp茅cifique au pays ou la configuration par d茅faut
  return (
    configs[country]?.[paymentMethod] ||
    configs.default[paymentMethod] || {
      steps: [
        {
          title: waitTitle,
          content: processingContent,
        },
      ],
      note: retryNote,
    }
  );
};

// Fonction pour rendre le contenu avec les codes mis en 茅vidence
const renderContentWithHighlights = (
  content: string,
  highlightCodes: string[]
): React.ReactNode => {
  let result: React.ReactNode = content;
  
  highlightCodes.forEach((code) => {
    const regex = new RegExp(code.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g");
    if (typeof result === 'string') {
      const parts = result.split(regex);
      result = parts.reduce((acc: React.ReactNode[], part, index, array) => {
        if (index === array.length - 1) {
          return [...acc, part];
        }
        return [
          ...acc,
          part,
          <span
            key={`${code}-${index}`}
            className={styles.highlightCode}
            onClick={() => {
              navigator.clipboard.writeText(code).then(() => {
                feedback.success("Copié !");
              });
            }}
          >
            {code}
          </span>,
        ];
      }, []);
    }
  });
  
  return result;
};

const PaymentConfirmModal: React.FC<PaymentConfirmModalProps> = ({
  visible,
  onClose,
  onConfirm,
  paymentMethod,
  loading = false,
  disabled = false,
  country,
}) => {
  const { t } = useTranslationPlus();
  const [verifyCode, setVerifyCode] = useState("");
  const [isAutoTriggered, setIsAutoTriggered] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);

  const config = getPaymentConfig(paymentMethod, country, t);
  const icon = getPaymentIcon(paymentMethod);

  // Whether payment method requires auto-trigger
  const shouldAutoTrigger =
    ["Wave", "Momo", "Moov", "Tmoney"].includes(paymentMethod) &&
    !config?.requiresVerifyCode &&
    !config?.disableAutoTrigger;

  // Clear timers
  const clearTimers = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  };

  // Start auto-trigger timer
  const startAutoTrigger = () => {
    if (disabled || !shouldAutoTrigger || !config) {
      return;
    }

    // Start countdown display
    setCountdown(2);
    countdownTimerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Auto-trigger after 2 seconds
    timerRef.current = setTimeout(() => {
      if (!isAutoTriggered) {
        setIsAutoTriggered(true);
        onConfirm();
      }
    }, 2000);
  };

  // When modal is visible, sync display state
  useEffect(() => {
    if (visible) {
      setIsAutoTriggered(false);
      setCountdown(0);
      if (shouldAutoTrigger) {
        startAutoTrigger();
      }
    } else {
      clearTimers();
      setIsAutoTriggered(false);
      setCountdown(0);
    }

    return () => {
      clearTimers();
    };
  }, [visible, paymentMethod, disabled]);

  // Clear timers on unmount
  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, []);

  if (!config) return null;

  const handleClose = () => {
    clearTimers();
    setVerifyCode(""); // Clear verify code on close
    setIsAutoTriggered(false);
    setCountdown(0);
    onClose();
  };

  const handleConfirm = () => {
    // If already auto-triggered, ignore manual click
    if (disabled || loading || isAutoTriggered) {
      return;
    }

    clearTimers(); // Clear timers to prevent duplicate trigger

    if (config.requiresVerifyCode) {
      onConfirm(verifyCode);
    } else {
      setIsAutoTriggered(true);
      onConfirm();
    }
  };

  // Whether confirm button should be disabled
  const requiredCodeLength = config?.verifyCodeLength || 4;
  const isConfirmDisabled =
    disabled ||
    loading ||
    isAutoTriggered ||
    (config?.requiresVerifyCode && verifyCode.length !== requiredCodeLength);

  // Confirm button text
  const getConfirmButtonText = () => {
    if (loading) {
      return t("Wallet.PaymentConfirm.ConfirmProcessing");
    }
    if (isAutoTriggered) {
      return t("Wallet.PaymentConfirm.ConfirmSent");
    }
    if (countdown > 0) {
      return t("Wallet.PaymentConfirm.ConfirmCountdown", { seconds: countdown });
    }
    return t("Wallet.PaymentConfirm.Confirm");
  };

  return (
    <PopupMask visible={visible} onClose={handleClose}>
      <div className={styles.paymentConfirmModal}>
        <div className={styles.modalHeader}>
          <div className={styles.paymentInfo}>
            {icon && (
              <img
                src={icon}
                alt={paymentMethod}
                className={styles.paymentIcon}
              />
            )}
            <span className={styles.paymentName}>{paymentMethod}</span>
          </div>
          <button className={styles.closeBtn} onClick={handleClose}>
            ×
          </button>
        </div>

        <div className={styles.modalContent}>
          <div className={styles.paymentInstructions}>
            <p className={styles.instructionTitle}>
              {t("Wallet.PaymentConfirm.InstructionTitle", {
                method: paymentMethod,
              })}
            </p>

            {config.steps.map((step, index) => (
              <div key={index} className={styles.instructionStep}>
                {step.title && (
                  <div className={styles.stepTitle}>{step.title}</div>
                )}
                {step.content && (
                  <div className={styles.stepContent}>
                    {step.hasHighlightCode && step.highlightCodes
                      ? renderContentWithHighlights(
                          step.content,
                          step.highlightCodes
                        )
                      : step.content}
                  </div>
                )}
              </div>
            ))}

            {config.requiresVerifyCode && (
              <div className={styles.verifyCodeSection}>
                <div className={styles.verifyCodeLabel}>{config.note}</div>
                <div className={styles.verifyCodeInput}>
                  <OTPInput
                    length={config.verifyCodeLength || 4}
                    theme="box"
                    value={verifyCode}
                    onChange={setVerifyCode}
                    autoFocus
                  />
                </div>
              </div>
            )}

            {!config.requiresVerifyCode && (
              <div className={styles.paymentNote}>{config.note}</div>
            )}
          </div>
        </div>

        <div className={styles.modalFooter}>
          <ButtonUI
            className={styles.confirmBtn}
            type="primary"
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
            loading={loading}
          >
            <RechargeCooldownLabel cooldownLabel={t(loading ? "Wallet.PaymentConfirm.ConfirmProcessing" : "Wallet.PaymentConfirm.Confirm")}>{getConfirmButtonText()}</RechargeCooldownLabel>
          </ButtonUI>
        </div>
      </div>
    </PopupMask>
  );
};

export default PaymentConfirmModal;


